import type { DiffScope, Session } from '@orgus/protocol';
import { DIFF_SCOPE_LABEL } from '@orgus/protocol';
import { FileDiff, GitBranch, GitCommitHorizontal, RotateCw, X } from '@orgus/ui/icons';
import { useState } from 'react';
import { Button, DiffViewer, EmptyState, ErrorState, GROUP_INDENT, GroupChevron, IconButton, Pressable, RelativeTime, Segmented, Skeleton, Tooltip, cn, formatInt, plural } from '@orgus/ui';
import type { DiffViewMode } from '@orgus/ui';
import { useDiff } from './api.ts';

function DiffSkeleton() {
  return (
    <div role="status" aria-label="Loading the diff" className="flex flex-col gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="overflow-hidden rounded-card border border-border bg-raised">
          <div className="flex h-row items-center gap-2 border-b border-border-subtle px-3">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-56" />
            <Skeleton className="ml-auto h-3 w-12" />
          </div>
          <div className="flex flex-col gap-2 p-3">
            {Array.from({ length: i === 0 ? 6 : 3 }, (_, j) => (
              <Skeleton key={j} className="h-3" style={{ width: `${40 + ((j * 37 + i * 11) % 50)}%` }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_COPY: Record<DiffScope, { title: string; description: string }> = {
  working: { title: 'No uncommitted changes', description: 'Changes appear here as the session edits files.' },
  branch: { title: 'No commits on this branch yet', description: 'Commits the session makes on this branch appear here, compared with the base branch.' },
  commit: { title: 'This commit changes no files', description: 'It may be a merge or an empty commit.' },
};

/**
 * The session checkout's changes: uncommitted work, the branch since its base, or one commit,
 * read live from the machine. Unified or side by side; large files start collapsed.
 */
export function DiffTab({ session, mode, onModeChange }: { session: Session; mode: DiffViewMode; onModeChange: (m: DiffViewMode) => void }) {
  const [scope, setScope] = useState<'working' | 'branch'>('working');
  const [commit, setCommit] = useState<string | null>(null);
  const [showCommits, setShowCommits] = useState(true);
  const effective: DiffScope = commit ? 'commit' : scope;
  const d = useDiff(session, effective, commit);
  const r = d.data;

  const scopeSwitch = (
    <Segmented<'working' | 'branch'>
      label="Which changes"
      value={scope}
      onChange={(v) => {
        setCommit(null);
        setScope(v);
      }}
      options={[
        { value: 'working', label: DIFF_SCOPE_LABEL.working },
        { value: 'branch', label: DIFF_SCOPE_LABEL.branch, ...(r?.commits.length ? { count: r.commits.length + r.commitsOmitted } : {}) },
      ]}
    />
  );

  const header = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
      {r?.branch && (
        <span className="inline-flex items-center gap-1">
          <GitBranch className="size-3.5 text-fg-subtle" aria-hidden />
          <span className="font-mono text-caption text-fg">{r.branch}</span>
        </span>
      )}
      {r?.base && effective === 'branch' && (
        <span>
          compared with <span className="font-mono text-caption text-fg">{r.base.ref}</span>
        </span>
      )}
      {r && (
        <span className="num">
          {plural(r.totals.files, 'file')}, <span className="text-passed">+{formatInt(r.totals.additions)}</span> <span className="text-failed">-{formatInt(r.totals.deletions)}</span>
        </span>
      )}
      {r && (
        <span className="ml-auto inline-flex items-center gap-1 text-caption text-fg-subtle">
          <RelativeTime ts={r.at} prefix="Read" />
          <IconButton label="Read the diff again" icon={<RotateCw className={cn(d.refreshing && 'animate-spin')} />} size="xs" onClick={d.reload} loading={false} />
        </span>
      )}
    </div>
  );

  let body;
  if (d.loading) body = <DiffSkeleton />;
  else if (d.error && !r) body = <ErrorState error={d.error} onRetry={d.reload} />;
  else if (r) {
    body = (
      <div className="flex flex-col gap-3">
        {d.error && <ErrorState variant="inline" error={d.error} onRetry={d.reload} />}
        {r.commit && (
          <div className="flex items-center gap-2 rounded-control border border-accent-line bg-accent-soft px-3 py-2 text-sm">
            <GitCommitHorizontal className="size-4 shrink-0 text-accent" aria-hidden />
            <span className="font-mono text-caption text-fg-muted">{r.commit.short}</span>
            <span className="min-w-0 flex-1 truncate text-fg">{r.commit.subject}</span>
            <span className="shrink-0 text-caption text-fg-subtle">
              <RelativeTime ts={r.commit.at} />
            </span>
            <IconButton label="Back to the whole branch" icon={<X />} size="xs" onClick={() => setCommit(null)} />
          </div>
        )}
        {effective === 'branch' && r.commits.length > 0 && (
          <div className="rounded-card border border-border bg-raised">
            <Pressable aria-expanded={showCommits} onClick={() => setShowCommits((o) => !o)} className="flex h-row items-center gap-2 rounded-card px-3 text-ui font-medium text-fg-muted hover:bg-hover">
              <GroupChevron open={showCommits} />
              {plural(r.commits.length + r.commitsOmitted, 'commit')} since {r.base?.ref ?? 'the base'}
            </Pressable>
            {showCommits && (
              // The commits sit one group step in, under the header's label (GROUP_INDENT).
              <ul className={cn('border-t border-border-subtle py-1', GROUP_INDENT)}>
                {r.commits.map((c) => (
                  <li key={c.sha}>
                    <Tooltip content="Show only this commit">
                      <Pressable onClick={() => setCommit(c.sha)} className="flex h-row items-center gap-2 px-3 text-ui hover:bg-hover">
                        <GitCommitHorizontal className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                        <span className="shrink-0 font-mono text-caption text-fg-subtle">{c.short}</span>
                        <span className="min-w-0 flex-1 truncate text-fg">{c.subject}</span>
                        <span className="shrink-0 text-caption text-fg-subtle">
                          <RelativeTime ts={c.at} />
                        </span>
                      </Pressable>
                    </Tooltip>
                  </li>
                ))}
                {r.commitsOmitted > 0 && <li className="px-3 py-1 text-caption text-fg-subtle">{plural(r.commitsOmitted, 'older commit')} not listed.</li>}
              </ul>
            )}
          </div>
        )}
        {r.note && (
          <p role="note" className="rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-fg-muted">
            {r.note}
          </p>
        )}
        {r.truncated && (
          <p role="note" className="rounded-control border border-waiting/30 bg-waiting-soft px-3 py-2 text-sm text-fg">
            Some changes are not shown: this diff passed the size limit for one view. File counts stay exact; open the checkout to see everything.
          </p>
        )}
        {r.files.length ? (
          <DiffViewer key={`${effective}:${commit ?? ''}`} files={r.files} mode={mode} onModeChange={onModeChange} toolbar={null} />
        ) : (
          !r.note && <EmptyState icon={<FileDiff />} title={EMPTY_COPY[effective].title} description={EMPTY_COPY[effective].description} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {scopeSwitch}
        {d.refreshing && <span className="text-caption text-fg-subtle">Reading</span>}
      </div>
      {r && header}
      {body}
      {r && !d.loading && session.status !== 'working' && r.files.length === 0 && effective === 'working' && (
        <Button size="sm" variant="ghost" className="self-start" onClick={() => setScope('branch')}>
          See the branch instead
        </Button>
      )}
    </div>
  );
}
